import { useEffect, useState } from "react";
import { Link } from "react-router";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { HERO_MEDIA, MARKET_PATH, MARKET_PORTAL } from "../../constants/simmarkets";
import Images from "../../utils/Images";
import { ArrowIcon, BrandMark, CornerMarks, EASE, SectionWash, useFade } from "./ui";

function MarketHero() {
  const { reduce } = useFade();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const fadeUp = {
    hidden: reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 },
    show: {
      opacity: 1,
      y: 0,
      transition: reduce ? { duration: 0 } : { duration: 0.55, ease: EASE },
    },
  };

  useEffect(() => {
    if (reduce || paused) return undefined;
    const id = window.setInterval(() => {
      setActive((step) => (step + 1) % MARKET_PATH.length);
    }, 3200);
    return () => window.clearInterval(id);
  }, [reduce, paused]);

  return (
    <section
      id="simmarkets-hero"
      data-tone="white"
      className="relative overflow-hidden bg-white text-navy"
      aria-labelledby="simmarkets-heading"
    >
      <SectionWash tone="white" />
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -left-24 top-28 h-[28rem] w-[28rem] rounded-full border border-market-green/15" />
        <div className="absolute -left-8 top-44 h-[18rem] w-[18rem] rounded-full border border-market-gold/30" />
        <p className="absolute -left-4 top-10 select-none whitespace-nowrap text-[8rem] font-black leading-none tracking-tight text-navy/[0.035] sm:text-[12rem] lg:top-4 lg:text-[14rem]">
          MARKET
        </p>
      </div>
      <div className="pointer-events-none absolute inset-x-0 top-0 z-[3] h-px bg-gradient-to-r from-transparent via-market-gold to-transparent" />

      <div className="relative mx-auto flex max-w-8xl flex-col gap-8 px-4 pb-12 pt-[5.5rem] sm:px-6 lg:flex-row lg:items-stretch lg:gap-8 lg:px-8 lg:pb-16 lg:pt-[6rem]">
        <motion.div
          className="relative z-[3] flex min-w-0 flex-col pt-6 [container-type:inline-size] lg:w-[54%] lg:shrink-0 lg:pt-8"
          initial="hidden"
          animate="show"
          variants={{
            hidden: {},
            show: {
              transition: {
                staggerChildren: reduce ? 0 : 0.09,
                delayChildren: reduce ? 0 : 0.06,
              },
            },
          }}
        >
          <motion.p
            className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40"
            variants={fadeUp}
          >
            <Link
              to="/"
              className="transition hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-market-gold"
            >
              Home
            </Link>
            <span className="mx-2 text-navy/20">/</span>
            Services
            <span className="mx-2 text-navy/20">/</span>
            SimMarket
          </motion.p>

          <motion.div className="mt-6" variants={fadeUp}>
            <BrandMark
              src={Images.simmarket_colored}
              alt="SimMarket"
              className="h-10 w-auto max-w-[16rem] sm:h-12"
            />
          </motion.div>

          <motion.p
            className="mt-5 max-w-xl text-sm font-medium leading-relaxed text-market-green sm:text-base"
            variants={fadeUp}
          >
            Intelligent Financial Marketplace, Consumer Education &amp; Qualified Lead Generation
            Platform
          </motion.p>

          <motion.h1
            id="simmarkets-heading"
            className="mt-5 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2.05rem,7cqi,4.05rem)]"
            variants={fadeUp}
          >
            <span className="block text-market-green">Learn. Compare.</span>
            <span className="block">Decide. Connect.</span>
          </motion.h1>

          <motion.p
            className="mt-5 max-w-2xl font-medium leading-snug text-navy/70 [font-size:clamp(1.05rem,3.2cqi,1.35rem)]"
            variants={fadeUp}
          >
            Africa&apos;s intelligent financial marketplace.
          </motion.p>
          <motion.p
            className="mt-2 max-w-2xl text-base leading-relaxed text-navy/55 sm:text-lg"
            variants={fadeUp}
          >
            Empowering consumers. Accelerating financial institutions. Transforming customer
            acquisition.
          </motion.p>

          <motion.div className="mt-8 flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-center" variants={fadeUp}>
            <a
              href={MARKET_PORTAL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-12 w-full shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-market-gold px-6 text-sm font-semibold tracking-tight text-navy shadow-[0_12px_28px_-12px_rgba(229,184,11,0.9)] transition hover:bg-[#f0c52a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-market-gold focus-visible:ring-offset-2 sm:w-auto"
            >
              Explore the Marketplace
              <ArrowIcon className="h-3.5 w-3.5" />
            </a>
            <a
              href="#demo"
              className="inline-flex h-12 w-full shrink-0 cursor-pointer items-center justify-center whitespace-nowrap rounded-full bg-market-green px-6 text-sm font-semibold tracking-tight text-white transition hover:bg-[#2c735f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-market-green focus-visible:ring-offset-2 sm:w-auto"
            >
              Request a Live Demo
            </a>
            <a
              href="#videos"
              className="inline-flex h-12 w-full shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full border border-navy/15 bg-white px-6 text-sm font-semibold tracking-tight text-navy transition hover:border-navy hover:bg-navy hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2 sm:w-auto"
            >
              <Play className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
              Watch Product Video
            </a>
          </motion.div>

          <motion.div variants={fadeUp} className="mt-8 max-w-[40rem] space-y-4">
            <p className="leading-[1.8] text-navy/75 [font-size:clamp(1rem,2.7cqi,1.125rem)]">
              SimMarket is the flagship marketplace solution within the SimFinance ecosystem that
              transforms how consumers discover, understand and access financial products while
              enabling financial institutions to acquire highly qualified, intent-driven customers.
            </p>
            <p className="leading-[1.8] text-navy/65 [font-size:clamp(0.98rem,2.5cqi,1.0625rem)]">
              Rather than simply listing products, SimMarket combines financial education,
              artificial intelligence, intelligent comparison tools and customer engagement
              technologies to help consumers make confident financial decisions while delivering
              measurable business growth for financial institutions.
            </p>
            <p className="leading-[1.8] text-navy/65 [font-size:clamp(0.98rem,2.5cqi,1.0625rem)]">
              Whether you&apos;re looking for insurance, loans, investments, mortgages, pensions or
              banking services, SimMarket provides one trusted platform to learn, compare and
              connect with verified providers.
            </p>
          </motion.div>
        </motion.div>

        <motion.div
          className="relative z-[2] min-h-[28rem] w-full min-w-0 lg:min-h-0 lg:flex-1"
          initial={reduce ? false : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={reduce ? { duration: 0 } : { duration: 0.55, ease: EASE, delay: 0.12 }}
        >
          <div className="relative h-full min-h-[28rem] lg:min-h-full">
            <CornerMarks />
            <div
              className="absolute inset-0 overflow-hidden border border-navy/10 bg-[#16352e]"
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
            >
              <HeroBackdrop src={HERO_MEDIA.src} alt={HERO_MEDIA.alt} />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0c1f1b] via-[#0c1f1b]/20 to-black/10" />
              <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/30 to-transparent" />
              <HeroStage active={active} onSelect={setActive} />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function HeroBackdrop({ src, alt }) {
  const [failed, setFailed] = useState(!src);

  if (failed) return null;

  return (
    <img
      src={src}
      alt={alt}
      className="absolute inset-0 h-full w-full object-cover object-[center_22%]"
      onError={() => setFailed(true)}
    />
  );
}

function HeroStage({ active, onSelect }) {
  return (
    <div className="absolute inset-0 z-[1] flex flex-col justify-between p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <a
          href={MARKET_PORTAL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center rounded-full bg-black/40 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/85 backdrop-blur-md transition hover:text-market-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-market-gold"
        >
          consumer.simmarkets.info
        </a>
      </div>

      <div className="rounded-2xl border border-white/12 bg-[#0c1f1b]/78 p-3 backdrop-blur-md sm:p-3.5">
        <ol className="grid grid-cols-4 gap-1.5">
          {MARKET_PATH.map((item, index) => {
            const isActive = index === active;
            return (
              <li key={item.title}>
                <button
                  type="button"
                  onMouseEnter={() => onSelect(index)}
                  onFocus={() => onSelect(index)}
                  onClick={() => onSelect(index)}
                  className={`flex min-h-12 w-full cursor-pointer flex-col items-start justify-center rounded-xl px-2.5 py-2 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-market-gold ${
                    isActive
                      ? "bg-market-gold text-navy"
                      : "bg-white/8 text-white hover:bg-white/14"
                  }`}
                >
                  <span className={`font-mono text-[10px] tracking-[0.16em] ${isActive ? "text-navy/50" : "text-white/45"}`}>
                    {item.n}
                  </span>
                  <span className="text-xs font-semibold tracking-tight sm:text-sm">{item.title}</span>
                </button>
              </li>
            );
          })}
        </ol>
        <p className="mt-2.5 px-1 text-sm leading-relaxed text-white/80" aria-live="polite">
          {MARKET_PATH[active].line}
        </p>
      </div>
    </div>
  );
}

export default MarketHero;
